import { html, LitElement, PropertyValues } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { GzFormFieldWebComponent } from '../../gzForm/formField';
import { GzInputWebComponent } from '../../gzInput/component/GzInputWebComponent';
import { GzDataTableWebComponent } from '../component/GzDataTableWebComponent';
import { GzButtonWebComponent } from '../../gzButton';
import { Subscription } from 'rxjs';
import { FormGroup } from '../../gzForm/formGroup/form-group';
import { RequiredValidator } from '../../gzForm/validators/required.validator';
import { EmailValidator } from '../../gzForm/validators/email.validator';
import { smallMock, SmallMockExclusionsTypes, SmallMockItem } from './smallMock';
import { getDataTableDefaultStrings } from '../types/strings';
import { v4 as uuidv4 } from 'uuid';

@customElement('data-table-form-story')
export class DataTableFormStory extends LitElement {
  @query('#some-input')
  someInput: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#email')
  email: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#data-table-form-field')
  dataTableFormField: GzFormFieldWebComponent<GzDataTableWebComponent<SmallMockItem>, SmallMockItem[]>;

  @query('#data-table')
  dataTable: GzDataTableWebComponent<SmallMockItem>;

  @query('#first-table-input')
  firstTableInput: GzInputWebComponent;

  @query('#second-table-input')
  secondTableInput: GzInputWebComponent;

  @query('#submit')
  submitBtn: GzButtonWebComponent;

  @state()
  private formGroup: FormGroup | null;

  @state()
  private subscription: Subscription;

  private dropdownOptions = Object.keys(SmallMockExclusionsTypes).map((key, i) => ({ id: i, title: key, value: key }));

  firstUpdated(changedProperties: PropertyValues) {
    super.firstUpdated(changedProperties);
    this.buildFormGroup();
  }

  private addItem() {
    this.dataTable.addItem({
      id: uuidv4(),
      scope: this.firstTableInput.value,
      exclusionsType: this.secondTableInput.value,
    });
  }

  private focusFirstInput() {
    this.firstTableInput.inputFocus();
  }

  private buildFormGroup() {
    this.someInput.Validators = [RequiredValidator('Required field')];
    this.email.Validators = [RequiredValidator('Required field'), EmailValidator('Please enter a valid email')];
    const config = {
      someInput: this.someInput,
      email: this.email,
      dataTable: this.dataTableFormField,
    };
    this.formGroup = FormGroup.build(config);

    this.subscription = this.formGroup.valid$.subscribe({
      next: valid => {
        this.submitBtn.disabled = !valid;
      },
    });
  }

  private handleFormSubmit() {
    if (!this.formGroup) {
      return;
    }

    console.log('form submitting the following: ', this.formGroup.value);
    this.formGroup.unsubscribe();
    this.subscription.unsubscribe();
  }

  render() {
    return html`
      <div class="wrapper">
        <gz-form-field label="Some input" id="some-input" required stacked>
          <gz-input gz-form-input type="text" placeholder="Some input"></gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-form-field label="Email" id="email" required stacked>
          <gz-input gz-form-input type="text" placeholder="Email"></gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <div style="display: flex; gap: 16px; margin: 32px 0 16px 0">
          <gz-input id="first-table-input" type="text" placeholder="Type scope here" inputWidth="240"></gz-input>
          <div style="width: 240px">
            <gz-dropdown
              id="second-table-input"
              options="${JSON.stringify(this.dropdownOptions)}"
              labelWidth="240"
              placeholder="Choose exclusions type"
            ></gz-dropdown>
          </div>
          <gz-icon-button
            icon="ds-icon-add"
            size="32"
            fontSize="24"
            class="default-icon"
            buttonType="square"
            buttonFill="primary"
            buttonColor="white"
            @click="${this.addItem}"
          ></gz-icon-button>
        </div>

        <gz-form-field id="data-table-form-field">
          <gz-data-table
            id="data-table"
            gz-form-input
            .items="${[]}"
            .columns="${smallMock.columns}"
            allowRowDeletion
            allowRowSelection
            allowRowEditing
            allowRowReordering
            .strings="${getDataTableDefaultStrings()}"
            @empty-state-footer-button-clicked="${this.focusFirstInput}"
          >
          </gz-data-table>
        </gz-form-field>

        <gz-button id="submit" @click="${this.handleFormSubmit}">Submit</gz-button>
      </div>
    `;
  }
}
